// Справка внутри системы — общий механизм для «Стройки» и «Бетона».
// Кнопка «?» в правом нижнем углу открывает окно: «Как сделать» (пошаговые сценарии по разделам),
// «Мои разделы» (что видно и что можно на каждом экране) и «Термины». Содержимое отбирается
// по ролям пользователя; справка помнит вкладку, открытый сценарий, поиск и прокрутку
// (в браузере, отдельно для каждого пользователя — общий компьютер или телефон на смене).

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { navigate } from "./router";
import { Button, Icon, Input, useEscape } from "./ui";

export type HelpPageInfo = { about: string; see: string[]; can: string[]; tip?: string };
export type HelpPage<R extends string, P extends string> = { page: P; title: string; icon: string; text: Partial<Record<R, HelpPageInfo>> };
export type HelpScenario<R extends string, P extends string> = { id: string; roles: R[]; page: P; title: string; steps: string[]; result?: string; note?: string };
export type HelpTerm<R extends string> = { term: string; roles: R[]; text: string };
export type HelpContent<R extends string, P extends string> = {
  app: string;
  pages: HelpPage<R, P>[];
  scenarios: HelpScenario<R, P>[];
  terms: HelpTerm<R>[];
  /** Роль, чьё описание раздела заменяет остальные (администратор), если оно есть. */
  primary?: R;
};
export type HelpProps<R extends string, P extends string> = {
  content: HelpContent<R, P>;
  userId: string;
  roles: R[];
  roleLabel: string;
  here: P;
  pathOf: (p: P) => string | null;
  searchHint?: string;
  pagesNote?: string | null;
};

type Tab = "pages" | "howto" | "terms";
type Saved = { tab: Tab; scenario: string | null; page: string | null; q: string; scroll: Partial<Record<Tab, number>> };

function loadSaved(key: string): Saved {
  const empty: Saved = { tab: "howto", scenario: null, page: null, q: "", scroll: {} };
  try { return { ...empty, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch { return empty; }
}
function storeSaved(key: string, v: Saved): void {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* хранилище недоступно — просто не запоминаем */ }
}

export function HelpButton<R extends string, P extends string>(props: HelpProps<R, P>) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="help-fab" aria-label="Справка: мои разделы и как что сделать" title="Справка" onClick={() => setOpen(true)}>?</button>
      {open && <HelpDialog {...props} onClose={() => setOpen(false)} />}
    </>
  );
}

function HelpDialog<R extends string, P extends string>({ content, userId, roles, roleLabel, here, pathOf, searchHint, pagesNote, onClose }: HelpProps<R, P> & { onClose: () => void }) {
  const key = `rbs.${content.app}.help.${userId}`;
  const [st, setSt] = useState<Saved>(() => loadSaved(key));
  const body = useRef<HTMLDivElement>(null);
  const scroll = useRef<Partial<Record<Tab, number>>>({ ...st.scroll });
  const set = (p: Partial<Saved>) => setSt((x) => ({ ...x, ...p }));
  useEffect(() => { storeSaved(key, { ...st, scroll: scroll.current }); }, [st, key]);
  const close = () => { scroll.current[st.tab] = body.current?.scrollTop || 0; storeSaved(key, { ...st, scroll: scroll.current }); onClose(); };
  useEscape(close);
  useLayoutEffect(() => { if (body.current) body.current.scrollTop = scroll.current[st.tab] || 0; }, [st.tab]);

  const has = (rs: R[]) => rs.some((x) => roles.includes(x));
  const infoFor = (p: HelpPage<R, P>): HelpPageInfo[] => {
    const prim = content.primary;
    if (prim && roles.includes(prim) && p.text[prim]) return [p.text[prim]!];
    return [...new Set(roles.filter((x) => p.text[x]).map((x) => p.text[x]!))];
  };
  const pages = content.pages.filter((p) => infoFor(p).length > 0);
  const order = [here, ...pages.map((p) => p.page).filter((x) => x !== here)].filter((p) => pages.some((x) => x.page === p) || content.scenarios.some((s) => s.page === p));
  const scenarios = content.scenarios.filter((s) => has(s.roles));
  const terms = content.terms.filter((t) => has(t.roles));
  const needle = st.q.trim().toLowerCase();
  const match = (...xs: (string | undefined)[]) => !needle || xs.some((x) => x?.toLowerCase().includes(needle));
  const shown = scenarios.filter((s) => match(s.title, ...s.steps, s.result, s.note));
  const groups = order.map((pg) => ({ page: pg, items: shown.filter((s) => s.page === pg) })).filter((g) => g.items.length);
  const pageTitle = (p: P) => content.pages.find((x) => x.page === p)?.title || "";
  const pageIcon = (p: P) => content.pages.find((x) => x.page === p)?.icon || "catalog";
  const go = (p: P) => { const to = pathOf(p); if (to) { close(); navigate(to); } };
  const switchTab = (t: Tab) => { scroll.current[st.tab] = body.current?.scrollTop || 0; set({ tab: t }); };

  return (
    <>
      <div className="overlay" onClick={close} />
      <div className="help-modal" role="dialog" aria-modal="true" aria-label="Справка">
        <header>
          <div className="grow">
            <h2>Справка</h2>
            <div className="muted small">Ваша роль: <b>{roleLabel}</b>. Здесь только то, что доступно вам.</div>
          </div>
          <Button variant="ghost" className="icon-btn" aria-label="Закрыть" onClick={close}>✕</Button>
        </header>
        <div className="help-tabs" role="tablist">
          {([["howto", "Как сделать"], ["pages", "Мои разделы"], ["terms", "Термины"]] as [Tab, string][]).map(([v, l]) => (
            <button key={v} role="tab" aria-selected={st.tab === v} onClick={() => switchTab(v)}>{l}</button>
          ))}
        </div>
        <div className="help-body" ref={body} onScroll={(e) => { scroll.current[st.tab] = e.currentTarget.scrollTop; }}>
          {st.tab === "howto" && (
            <div className="stack">
              <Input type="search" placeholder={searchHint || "Поиск по сценариям"} value={st.q} onChange={(e) => set({ q: e.target.value })} aria-label="Поиск по сценариям" />
              {groups.length === 0 && <p className="muted">Ничего не найдено. Попробуйте другое слово.</p>}
              {groups.map((g) => (
                <section key={g.page} className="help-group" aria-label={pageTitle(g.page)}>
                  <h3><Icon name={pageIcon(g.page)} />{pageTitle(g.page)}<span className="muted"> · {g.items.length}</span>{g.page === here && <span className="tag plain">вы здесь</span>}</h3>
                  {g.items.map((s) => (
                    <ScenarioCard key={s.id} s={s} open={st.scenario === s.id || !!needle} here={s.page === here}
                      canGo={!!pathOf(s.page)} pageTitle={pageTitle(s.page)} onGo={() => go(s.page)}
                      onToggle={() => set({ scenario: st.scenario === s.id ? null : s.id })} />
                  ))}
                </section>
              ))}
            </div>
          )}
          {st.tab === "pages" && (
            <div className="stack tight">
              {order.map((pg) => pages.find((p) => p.page === pg)).filter((p): p is HelpPage<R, P> => !!p).map((p) => (
                <PageCard key={p.page} title={p.title} icon={p.icon} infos={infoFor(p)} here={p.page === here}
                  open={st.page === p.page} canGo={!!pathOf(p.page)} onGo={() => go(p.page)}
                  onToggle={() => set({ page: st.page === p.page ? null : p.page })} />
              ))}
              {pagesNote && <p className="muted small">{pagesNote}</p>}
            </div>
          )}
          {st.tab === "terms" && (
            <dl className="help-terms">
              {terms.map((t) => <div key={t.term}><dt>{t.term}</dt><dd>{t.text}</dd></div>)}
            </dl>
          )}
        </div>
      </div>
    </>
  );
}

/** Сценарий-аккордеон. Отдельный компонент: при раскрытии карточка не пересоздаётся и прокрутка не прыгает. */
function ScenarioCard({ s, open, here, canGo, pageTitle, onGo, onToggle }: {
  s: { title: string; steps: string[]; result?: string; note?: string }; open: boolean; here: boolean; canGo: boolean; pageTitle: string; onGo: () => void; onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (open) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [open]);
  return (
    <div ref={ref} className={`help-card${open ? " open" : ""}`}>
      <button type="button" className="help-q" aria-expanded={open} onClick={onToggle}>
        <span>{s.title}</span><span className="chev" aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="help-a">
          <ol>{s.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>
          {s.result && <p><b>Результат.</b> {s.result}</p>}
          {s.note && <p className="muted">{s.note}</p>}
          {!here && canGo && <Button size="small" onClick={onGo}>Открыть «{pageTitle}»</Button>}
        </div>
      )}
    </div>
  );
}

function PageCard({ title, icon, infos, here, open, canGo, onGo, onToggle }: {
  title: string; icon: string; infos: HelpPageInfo[]; here: boolean; open: boolean; canGo: boolean; onGo: () => void; onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (open) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [open]);
  return (
    <div ref={ref} className={`help-page${here ? " here" : ""}${open ? " open" : ""}`}>
      <button type="button" className="help-q" aria-expanded={open} onClick={onToggle}>
        <span className="row tight"><Icon name={icon} />{title}{here && <span className="tag plain">вы здесь</span>}</span>
        <span className="chev" aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      {!open && <div className="help-sum small muted">{infos[0].about}</div>}
      {open && (
        <div className="help-a">
          {infos.map((i, n) => (
            <div key={n} className="stack tight">
              <p style={{ margin: 0 }}>{i.about}</p>
              <div><b className="small">Что здесь видно</b><ul>{i.see.map((x, k) => <li key={k}>{x}</li>)}</ul></div>
              <div><b className="small">Что вы можете делать</b><ul>{i.can.map((x, k) => <li key={k}>{x}</li>)}</ul></div>
              {i.tip && <p className="help-tip small">{i.tip}</p>}
            </div>
          ))}
          {!here && canGo && <Button size="small" onClick={onGo}>Открыть «{title}»</Button>}
        </div>
      )}
    </div>
  );
}
